import React, { useState, useEffect } from "react";
import {
    Container,
    Row,
    Col,
    Modal,
    Tabs,
    Tab,
    Button,
    Breadcrumb,
    Spinner
} from "react-bootstrap";
import { NavLink, useParams, useNavigate } from "react-router-dom";
import Base from '../Base';
import PortWiseTab from './PortWiseTab';
import { TiTick } from "react-icons/ti";
import { CgTranscript } from "react-icons/cg";
import { VscGitFetch } from "react-icons/vsc";
import { ImUpload2, ImDownload2 } from "react-icons/im";
import { FaAngleDoubleUp, FaAngleDoubleDown } from "react-icons/fa";
import instance from "../../services/baseService";
import useCheckAuthorization from "../../hooks/checkAuthorization";
import { CRUD_PERMISSIONS } from "../../constants/user";
import toast from "react-hot-toast";
import InwardOutwardModal from "./InwardOutwardModal";
import useSidebar from "../../hooks/useSidebar";
import { useDispatch } from "react-redux";
import { showLoader } from "../../slices/commonReducer";
import { FaArrowLeft, FaArrowRight } from "react-icons/fa";
function PortWiseReport() {
    const [activeTab, setActiveTab] = useState(null);
    const { client_id, report_id } = useParams();
    const [portCodes, setPortCodes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [tableData, setTableData] = useState([]);
    const [loadingData, setLoadingData] = useState(false);
    const [reportData, setReportData] = useState({});
    const [pageSize, setPageSize] = useState(50);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    const [portCodeData, setPortCodeData] = useState({ gstNo: null, scripNo: null, scripDate: null, iecCode: null, status: 0, iceGate_approve: null, dgft_approve: null, portcode_id: null, approval_request: 0, total_count: 0, total_amount: 0, scrip_cancelled: 0 })
    const approvePermission = useCheckAuthorization(
        CRUD_PERMISSIONS.reconciliation.approve
    );
    const [showInwardOutwardModal, setShowInwardOutwardModal] = useState(false);
    const [modalType, setModalType] = useState("");
    const { collapse, expand, isSidebarCollapsed } = useSidebar();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    useEffect(() => {
        fetchPortCodes();
    }, [client_id, report_id]);

    useEffect(() => {
        if (activeTab && client_id && report_id) {
            setCurrentPage(1); // Reset to first page on tab change
            fetchPortCodeData(activeTab, 1, pageSize);
        }
    }, [activeTab, client_id, report_id, pageSize]);

    const fetchPortCodes = async () => {
        try {
            setLoading(true);
            const response = await instance.post(`/api/v1/reconciliation/port-code/list/${client_id}/${report_id}`);

            if (response.data.type === "success") {
                const codes = response.data.data.port_codes || [];
                setPortCodes(codes);

                // Check if current activeTab still exists in the new list
                const activeTabExists = codes.some(code => code.port_code_id === activeTab);

                if (codes.length > 0) {
                    if (!activeTab || !activeTabExists) {
                        // If no active tab or deleted tab, set to first item
                        setActiveTab(codes[0].port_code_id);
                    }
                    // If activeTab still exists, it will remain selected
                } else {
                    // No codes left, clear everything
                    setActiveTab(null);
                    setTableData([]);
                    setPortCodeData({
                        gstNo: null,
                        scripNo: null,
                        scripDate: null,
                        iecCode: null,
                        status: 0,
                        iceGate_approve: null,
                        dgft_approve: null,
                        portcode_id: null,
                        approval_request: 0
                    });
                }
            }
        } catch (error) {
            console.error("Error fetching port codes:", error);
            setPortCodes([]);
            setActiveTab(null);
            setTableData([]);
        } finally {
            setLoading(false);
        }
    };

    const fetchPortCodeData = async (portCodeId, page = currentPage, limit = pageSize) => {
        try {
            setLoadingData(true);
            const response = await instance.post(`/api/v1/reconciliation/port-code/data/${client_id}/${report_id}`, {
                port_code_id: parseInt(portCodeId),
                page: page,
                limit: limit
            });

            if (response.data.type === "success") {
                setReportData(response.data.data.uploaded_file)
                setPortCodeData({
                    gstNo: response.data.data.gstNo,
                    scripNo: response.data.data.scripNo,
                    scripDate: response.data.data.scrip_date,
                    iecCode: response.data.data.iec_code,
                    status: response.data.data.status,
                    iceGate_approve: response.data.data.iceGate_approve === 0 ? null : response.data.data.iceGate_approve,
                    dgft_approve: response.data.data.dgft_approve === 0 ? null : response.data.data.dgft_approve,
                    portcode_id: response.data.data.portcode_id,
                    approval_request: response.data.data.approval_request || 0,
                    total_count: response.data.data.total_count || 0,
                    total_amount: response.data.data.total_amount || 0,
                    scrip_cancelled: response.data.data.scrip_cancelled || 0,
                })
                setTotalPages(response.data.data.total_pages || 1);
                setCurrentPage(page);
                const records = response.data.data.lists || [];
                const mappedData = records.map((record) => ({
                    sbNo: record.sb_no || "",
                    sbDate: record.sb_date || "",
                    scrollNo: record.scroll_no || "N/A",
                    status: record.status || null,
                    type: record.amount_type || "",
                    amount: record.amount || "",
                    ifsc_code: record.ifsc_code || "",
                    brc_data: record.brc_data || [],

                }));
                setTableData(mappedData);
            }
        } catch (error) {
            console.error("Error fetching port code data:", error);
            setTableData([]);
        } finally {
            setLoadingData(false);
        }
    };

    const handleApproval = async (clientId, fileId, portCodeId, approvalType, approve) => {
        try {
            setLoadingData(true);
            const response = await instance.post(
                `/api/v1/reconciliation/port-code/approve/${clientId}/${fileId}/${portCodeId}`,
                {
                    approval_type: approvalType, // 1 = IceGate, 2 = DGFT
                    approve: approve             // 1 = Approve, 0 = Reject
                }
            );

            toast.success(response.data.message);
            fetchPortCodeData(activeTab);
        } catch (error) {
            console.error("Approval API Error:", error);
            toast.error(error.response?.data?.message || "Approval failed");
        } finally {
            setLoadingData(false);
        }
    };

    const handleDownloadExcel = async (clientId, fileId, portCodeId, downloadBrcExcel) => {
        try {
            setLoadingData(true);
            const response = await instance.post(
                `/api/v1/reconciliation/port-code/excel/download/${clientId}/${fileId}/${portCodeId}`,
                {
                    include_brc_data: downloadBrcExcel,
                },
                {
                    responseType: 'blob',
                }
            );

            const blob = new Blob([response.data], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            });

            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'PORTCODE_DATA.xlsx'); // Just use a static name
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            toast.success('Excel file downloaded successfully');
            fetchPortCodeData(activeTab);
        } catch (error) {
            console.error("Download API Error:", error);
            toast.error(error.response?.data?.message || "Download failed");
        } finally {
            setLoadingData(false);
        }
    };

    const handleSessionGenerate = async (process) => {
        try {
            setLoadingData(true);
            localStorage.removeItem('session_id');
            localStorage.removeItem('process_type');
            const portcodeId = portCodeData.portcode_id

            const response = await instance.post(
                `/api/v1/reconciliation/session/create/${client_id}/${report_id}`,
                {
                    process_type: process,
                    portcode_id: portcodeId
                }
            );

            if (response.data.type === "success") {
                toast.success(response.data.message);
                dispatch(showLoader('Running automation task...'));
                // localStorage.setItem('session_id', response.data.data.session_id);
                // localStorage.setItem('process_type', response.data.data.process_type);
                // process === "SCRIP_GENERATION"
                //     ? window.open("https://foservices.icegate.gov.in/#/login", "_blank", "noopener")
                //     : process === "PROCESS_BRC" && window.open("https://www.dgft.gov.in/CP/", "_blank", "noopener");

            }
            else {
                toast.error(response.data?.message || "Error Occurred");
            }
        } catch (error) {
            console.error("Error creating automation session:", error);
            const errorMessage = error.response?.data?.message || 'Failed. Please try again.';
            toast.error(errorMessage);
        } finally {
            setLoadingData(false);
        }
    };


    const handleRequestApproval = async (clientId, reportId, portcodeId) => {
        try {
            setLoadingData(true);
            const response = await instance.post(
                `/api/v1/reconciliation/port-code/approval_request/${clientId}/${reportId}/${portcodeId}`,
            );

            toast.success(response.data.message);
            fetchPortCodeData(activeTab);
        } catch (error) {
            console.error("Approval API Error:", error);
            toast.error(error.response?.data?.message || "Approval failed");
        } finally {
            setLoadingData(false);
        }
    };

    const handlePageSizeChange = (newSize) => {
        setPageSize(newSize);
        setCurrentPage(1);
        fetchPortCodeData(activeTab, 1, newSize);
    };

    const handlePrevPage = () => {
        if (currentPage > 1) {
            const newPage = currentPage - 1;
            setCurrentPage(newPage);
            fetchPortCodeData(activeTab, newPage, pageSize);
        }
    };

    const handleNextPage = () => {
        if (currentPage < totalPages) {
            const newPage = currentPage + 1;
            setCurrentPage(newPage);
            fetchPortCodeData(activeTab, newPage, pageSize);
        }
    };

    const handleClose = () => {
        expand();
        setShowInwardOutwardModal(false)
    }
    return (
        <Base>
            <Container fluid className="p-4 bg-light min-vh-100">
                <Row>
                    <Col>
                        <Breadcrumb>
                            <Breadcrumb.Item linkAs={NavLink} linkProps={{ to: "/" }}>
                                Dashboard
                            </Breadcrumb.Item>
                            <Breadcrumb.Item linkAs={NavLink} linkProps={{ to: "/clients" }}>
                                Clients
                            </Breadcrumb.Item>
                            <Breadcrumb.Item
                                linkAs={NavLink}
                                linkProps={{ to: `/clients/tax-forms/${client_id}` }}
                            >
                                Tax-Forms
                            </Breadcrumb.Item>
                            <Breadcrumb.Item linkAs={NavLink} linkProps={{ to: `/RoDTEP/RoSCTL/${client_id}` }}>
                                RoDTEP/RoSCTL
                            </Breadcrumb.Item>
                            <Breadcrumb.Item linkAs={NavLink} linkProps={{ to: `/RoDTEP/RoSCTL/status/${client_id}` }}>
                                Upload Status
                            </Breadcrumb.Item>
                            <Breadcrumb.Item
                                onClick={() => navigate(-1)}
                                style={{ cursor: "pointer" }}   // 
                            >
                                Reports
                            </Breadcrumb.Item>
                            <Breadcrumb.Item active>Port Report</Breadcrumb.Item>
                        </Breadcrumb>
                    </Col>
                </Row>

                {/* ======== Card ======== */}
                <Row>
                    <Col>
                        <div className="bg-white p-4">
                            <div className="">
                                <h1 className="h4 fw-bold mb-2">Highest Value Report</h1>
                                <p className="text-muted small">{reportData.start_date} - {reportData.end_date}</p>
                            </div>

                            {loading ? (
                                <div className="text-center py-5">
                                    <Spinner animation="border" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </Spinner>
                                </div>
                            ) : portCodes.length > 0 ? (
                                <Tabs
                                    activeKey={activeTab}
                                    onSelect={(k) => setActiveTab(k || portCodes[0]?.port_code_id)}
                                    className="mb-4 custom-tabs scrollable-tabs"
                                >
                                    {/* Dynamic Tabs Generated from API Response */}
                                    {portCodes.map((portCodeObj) => (
                                        <Tab
                                            key={portCodeObj.port_code_id}
                                            eventKey={portCodeObj.port_code_id}
                                            title={`Portcode: ${portCodeObj.port_code}`}
                                        >
                                            <PortWiseTab
                                                portCode={portCodeObj.port_code}
                                                portCodeId={activeTab}
                                                clientId={client_id}
                                                reportId={report_id}
                                                scripNo={portCodeData.scripNo}
                                                scripDate={portCodeData.scripDate}
                                                gstNo={portCodeData.gstNo}
                                                portNo=""
                                                iecCode={portCodeData.iecCode}
                                                tableHeaders={["Sl No", "SB NO", "SB Date", "Scroll No", "Status", "Type", "Amount", "FOB", "BRC NO", "BRC Date", "BRC Realised Value", "IFSC Code"]}
                                                tableData={tableData}
                                                loading={loadingData}
                                                callback={fetchPortCodes}
                                                reportData={reportData}
                                                totalCount={portCodeData.total_count}
                                                totalAmount={portCodeData.total_amount}


                                            />
                                        </Tab>
                                    ))}
                                </Tabs>
                            ) : (
                                <div className="text-center py-5">
                                    <p className="text-muted">No port codes found for this period</p>
                                </div>
                            )}

                            {portCodes.length > 0 && (
                                <div className="d-flex justify-content-between align-items-center mt-4 gap-2 " >
                                    {/* Pagination Section */}
                                    <div className="d-flex align-items-center gap-2">
                                        <Button
                                            variant="outline-secondary"
                                            size="sm"
                                            onClick={handlePrevPage}
                                            disabled={currentPage === 1 || loadingData}
                                        >
                                            <FaArrowLeft />
                                        </Button>

                                        <select
                                            className="form-select form-select-sm"
                                            style={{ width: 'auto' }}
                                            value={pageSize}
                                            onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                                            disabled={true}
                                        >
                                            <option value={10}>10 per page</option>
                                            <option value={50}>50 per page</option>
                                            <option value={100}>100 per page</option>
                                        </select>

                                        <Button
                                            variant="outline-secondary"
                                            size="sm"
                                            onClick={handleNextPage}
                                            disabled={currentPage === totalPages || loadingData}
                                        >
                                            <FaArrowRight />
                                        </Button>

                                        <span className="text-muted small">
                                            Page {currentPage} of {totalPages}
                                        </span>

                                        {portCodeData.scrip_cancelled === 1 && (
                                            <div className="px-3 py-2 rounded-pill fw-bold  !text-red-700 !bg-red-100"
                                            >
                                                Scrip was once cancelled
                                            </div>
                                        )}
                                    </div>

                                    <div className="d-flex gap-2">
                                        <Button
                                            variant="primary"
                                            className="fw-bold px-4 shadow-sm d-flex align-items-center gap-1"
                                            onClick={() => handleDownloadExcel(
                                                client_id,
                                                report_id,
                                                portCodeData.portcode_id,
                                                portCodeData.status === 0 ? false : true
                                            )}
                                            disabled={loadingData}
                                        >
                                            <ImDownload2 />
                                            Download Excel
                                        </Button>
                                        {/* ========== STATUS 0, 10, OR 3: Approval Stages ========== */}
                                        {(portCodeData.status === 0 || portCodeData.status === 10 || portCodeData.status === 3) && (
                                            <>
                                                {(() => {
                                                    const isIceGateStage = portCodeData.status === 0;
                                                    const isDGFTStage = portCodeData.status === 3;
                                                    const isScripStage = portCodeData.status === 10;
                                                    const currentApproval = isIceGateStage
                                                        ? portCodeData.iceGate_approve
                                                        : isScripStage
                                                            ? portCodeData.scrip_approve  // NEW
                                                            : portCodeData.dgft_approve;
                                                    const approvalType = isIceGateStage ? 1 : isScripStage ? 2 : 3;
                                                    const downloadBrcExcel = isIceGateStage ? false : true;

                                                    // Case 1: Director Rejected (Show for both users)
                                                    if (currentApproval === 2) {
                                                        return (
                                                            <div className="px-3 py-2 rounded-pill fw-bold text-white"
                                                                style={{ backgroundColor: "#dc3545" }}>
                                                                Rejected
                                                            </div>
                                                        );
                                                    }

                                                    // Case 2: No approval request yet (Show "Request Approval" for both users)
                                                    if (portCodeData.approval_request === 0) {
                                                        return (
                                                            <>
                                                                {/* <Button
                                                                    variant="primary"
                                                                    className="fw-bold px-4 shadow-sm d-flex align-items-center gap-1"
                                                                    onClick={() => handleDownloadExcel(
                                                                        client_id,
                                                                        report_id,
                                                                        portCodeData.portcode_id,
                                                                        downloadBrcExcel
                                                                    )}
                                                                    disabled={loadingData}
                                                                >
                                                                    <ImDownload2 />
                                                                    Download Excel
                                                                </Button> */}
                                                                <Button
                                                                    variant="primary"
                                                                    className="fw-bold px-4 shadow-sm d-flex align-items-center gap-1"
                                                                    onClick={() => handleRequestApproval(
                                                                        client_id,
                                                                        report_id,
                                                                        portCodeData.portcode_id
                                                                    )}
                                                                    disabled={loadingData}
                                                                >
                                                                    <TiTick />
                                                                    Request Approval
                                                                </Button>
                                                            </>
                                                        );
                                                    }

                                                    // Case 3: Approval requested (approval_request = 1)
                                                    if (portCodeData.approval_request === 1) {
                                                        // Users WITHOUT approve permission: Show waiting message
                                                        if (!approvePermission) {
                                                            return (
                                                                <div className="px-3 py-2 rounded-pill fw-bold text-yellow-700"
                                                                    style={{ backgroundColor: "#fff3cd" }}>
                                                                    Waiting for Approval
                                                                </div>
                                                            );
                                                        }

                                                        // Users WITH approve permission: Show Approve/Reject buttons
                                                        return (
                                                            <>
                                                                {/* <Button
                                                                    variant="primary"
                                                                    className="fw-bold px-4 shadow-sm d-flex align-items-center gap-1"
                                                                    onClick={() => handleDownloadExcel(
                                                                        client_id,
                                                                        report_id,
                                                                        portCodeData.portcode_id,
                                                                        downloadBrcExcel
                                                                    )}
                                                                    disabled={loadingData}
                                                                >
                                                                    <ImDownload2 />
                                                                    Download Excel
                                                                </Button> */}
                                                                <Button
                                                                    variant="success"
                                                                    className="fw-bold px-4 shadow-sm d-flex align-items-center gap-1"
                                                                    onClick={() => handleApproval(
                                                                        client_id,
                                                                        report_id,
                                                                        portCodeData.portcode_id,
                                                                        approvalType,
                                                                        1 // Approve
                                                                    )}
                                                                    disabled={loadingData}
                                                                >
                                                                    <TiTick />
                                                                    Approve
                                                                </Button>
                                                                <Button
                                                                    variant="danger"
                                                                    className="fw-bold px-4 shadow-sm d-flex align-items-center gap-1"
                                                                    onClick={() => handleApproval(
                                                                        client_id,
                                                                        report_id,
                                                                        portCodeData.portcode_id,
                                                                        approvalType,
                                                                        2 // Reject
                                                                    )}
                                                                    disabled={loadingData}
                                                                >
                                                                    <TiTick />
                                                                    Reject
                                                                </Button>
                                                            </>
                                                        );
                                                    }

                                                    return null;
                                                })()}
                                            </>
                                        )}
                                        

                                        {/* ========== STATUS 1: Scrip Generation ========== */}
                                        {portCodeData.status === 1 && (
                                            <Button
                                                variant="primary"
                                                className="fw-bold px-4 shadow-sm d-flex align-items-center gap-1"
                                                onClick={() => handleSessionGenerate("SCRIP_GENERATION")}
                                                disabled={loadingData}
                                            >
                                                <CgTranscript />
                                                {loadingData ? 'Processing...' : 'Scrip Generate'}
                                            </Button>
                                        )}

                                        {/* ========== STATUS 11: BRC Processing ========== */}
                                        {portCodeData.status === 11 && (
                                            <Button
                                                variant="primary"
                                                className="fw-bold px-4 shadow-sm d-flex align-items-center gap-1"
                                                onClick={() => handleSessionGenerate("PROCESS_BRC")}
                                                disabled={loadingData}
                                            >
                                                <VscGitFetch />
                                                {loadingData ? 'Processing...' : 'Process BRC'}
                                            </Button>
                                        )}

                                        {/* ==========  STATUSES 4: Generate Letter ========== */}
                                        {portCodeData.status === 4 && (
                                            <>
                                                <Button
                                                    variant="primary"
                                                    className="fw-bold px-4 shadow-sm d-flex align-items-center gap-1"
                                                    disabled={loadingData}
                                                    onClick={() => {
                                                        setShowInwardOutwardModal(true);
                                                        collapse();
                                                    }}
                                                >
                                                    <FaAngleDoubleDown />
                                                    View or Generate Transfer Letters
                                                </Button>
                                            </>
                                        )}
                                        {/* ========== STATUS 5: Rejected (Terminal State) ========== */}
                                        {portCodeData.status === 5 && (
                                            <div className="px-3 py-2 rounded-pill fw-bold text-white"
                                                style={{ backgroundColor: "#dc3545" }}>
                                                Workflow Rejected
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </Col>
                </Row>
                <InwardOutwardModal
                    show={showInwardOutwardModal}
                    onHide={() => handleClose()}
                    clientId={client_id}
                    reportId={report_id}
                    port_code_id={portCodeData.portcode_id}
                />
            </Container>
        </Base>
    );
}

export default PortWiseReport;